import { Link, router } from "@inertiajs/react"
import {
  AlertCircle,
  AlertTriangle,
  Building2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Info,
  Sparkles,
  Wand2,
} from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import type {
  IssueSeverity,
  IssueType,
  QualityIssueActions,
  QualityIssueCard,
  QualitySuggestedFix,
} from "@/lib/types"

import { FieldDiff } from "./FieldDiff"

interface Props {
  issue: QualityIssueCard
  actions: QualityIssueActions
  studioUrl: string  // fallback target for "Open" when no property_show URL
  selected: boolean
  onToggleSelect: (id: number) => void
}

const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  stale_poi: "Stale POI",
  weak_metadata: "Weak metadata",
  coverage_gap: "Coverage gap",
  duplicate: "Duplicate",
  hotel_gap: "Hotel gap",
}

// Severity badge: ALWAYS text+icon (not color-only — a11y).
function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  const config: Record<IssueSeverity, {
    label: string
    Icon: typeof AlertCircle
    className: string
  }> = {
    critical: {
      label: "Critical",
      Icon: AlertCircle,
      className: "bg-destructive/10 text-destructive border-destructive/30",
    },
    high: {
      label: "High",
      Icon: AlertTriangle,
      className:
        "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
    },
    medium: {
      label: "Medium",
      Icon: Info,
      className: "bg-muted text-muted-foreground border-border",
    },
  }
  const { label, Icon, className } = config[severity]
  return (
    <Badge
      variant="outline"
      className={className}
      aria-label={`Severity: ${label}`}
      data-severity={severity}
    >
      <Icon className="size-3" aria-hidden />
      <span>{label}</span>
    </Badge>
  )
}

function daysSince(iso: string | undefined): number | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  return Math.floor((Date.now() - then) / 86_400_000)
}

function shortDate(iso: string | undefined): string {
  if (!iso) return ""
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return iso
  }
}

// Problem-line text built from suggested_fix payload (issue-type specific).
function problemText(issue: QualityIssueCard): string {
  const fix: QualitySuggestedFix = issue.suggested_fix ?? {}
  const subject = issue.subject_name ?? `#${issue.subject_id}`
  switch (issue.issue_type) {
    case "stale_poi": {
      const lv = fix.last_verified_at
      const age = daysSince(lv)
      return age !== null
        ? `Hours unverified ${age} days (last: ${shortDate(lv)})`
        : "POI metadata has not been verified recently"
    }
    case "weak_metadata": {
      const missing = (fix.missing_fields ?? []) as string[]
      return missing.length > 0
        ? `Missing: ${missing.join(", ")}`
        : "POI is missing key metadata fields"
    }
    case "hotel_gap":
      return `${subject} guests engaged with categories the regional pool doesn't cover yet`
    case "duplicate":
      return typeof fix.similarity === "number"
        ? `Possible duplicate (similarity ${Math.round(fix.similarity * 100)}%)`
        : "Possible duplicate"
    case "coverage_gap":
      return "Coverage gap detected"
    default:
      return "Issue detected"
  }
}

function priorityReason(issue: QualityIssueCard): string | null {
  const fix = issue.suggested_fix ?? {}
  if (typeof fix.priority_reason === "string" && fix.priority_reason.length) {
    return fix.priority_reason
  }
  switch (issue.issue_type) {
    case "stale_poi":
      return "Stale POIs reduce guide reliability for guests"
    case "weak_metadata":
      return "Weak metadata limits AI-assisted enrichment and guest filtering"
    case "hotel_gap":
      if (typeof fix.property_demand === "number") {
        return `${fix.property_demand} guest engagements with this category`
      }
      return "Property guests engaged with categories with thin regional supply"
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// IssueCard
// ---------------------------------------------------------------------------

export function IssueCard({
  issue,
  actions,
  studioUrl,
  selected,
  onToggleSelect,
}: Props) {
  const [open, setOpen] = useState(false)

  const reason = priorityReason(issue)

  const handleDismiss = () => {
    router.post(
      actions.dismiss,
      { reason: "out_of_scope" },
      {
        preserveScroll: true,
        onSuccess: () => {
          toast("Dismissed", {
            description: `${issue.subject_name ?? "Issue"} hidden from the worklist`,
            // Undo endpoint not yet wired — TODO Part C.
          })
        },
      },
    )
  }

  const handleApplyFix = () => {
    router.post(actions.apply_fix, {}, { preserveScroll: true })
  }

  const handleAcceptAll = (acceptedFields: string[]) => {
    router.post(
      actions.accept_suggestion,
      { accepted_fields: acceptedFields },
      { preserveScroll: true },
    )
  }

  return (
    <Card
      data-issue-id={issue.id}
      data-issue-type={issue.issue_type}
      className={selected ? "ring-2 ring-primary/40" : undefined}
    >
      <CardContent className="space-y-3 py-4">
        {/* Top row: badges */}
        <div className="flex items-center gap-2">
          <SeverityBadge severity={issue.severity} />
          <Badge variant="secondary">{ISSUE_TYPE_LABELS[issue.issue_type]}</Badge>
        </div>

        {/* Subject line */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-foreground">
              {issue.subject_name ?? `${issue.subject_type} #${issue.subject_id}`}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{problemText(issue)}</p>
            {reason && (
              <p className="mt-1 text-xs text-muted-foreground">
                <span className="font-medium">Why prioritized: </span>
                {reason}
              </p>
            )}
          </div>
        </div>

        {/* Suggested-fix block (per issue_type) */}
        <SuggestedFixBlock
          issue={issue}
          onApplyFix={handleApplyFix}
          propertyShowUrl={actions.property_show}
          open={open}
          onOpenChange={setOpen}
          onAcceptAll={handleAcceptAll}
        />

        {/* Bottom row: select + dismiss + open + apply */}
        <div className="flex items-center justify-between gap-2 pt-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect(issue.id)}
              aria-label={`Select ${issue.subject_name ?? "issue"} for batch action`}
            />
            <span>Select</span>
          </label>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="min-h-9"
              aria-label="Dismiss this issue"
            >
              Dismiss
            </Button>

            {/* Open opens the relevant editor. For hotel_gap → property page;
                for stale/weak → region Studio page (T8 V1 fallback — direct
                PoiSheet deep-link is a Part C follow-up). */}
            <Button asChild size="sm" variant="outline" className="min-h-9">
              <Link href={actions.property_show ?? studioUrl}>
                <ExternalLink className="mr-1 size-3.5" aria-hidden />
                Open
              </Link>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// SuggestedFixBlock — per-issue-type "Suggested fix" section.
// ---------------------------------------------------------------------------

interface SuggestedFixBlockProps {
  issue: QualityIssueCard
  onApplyFix: () => void
  propertyShowUrl?: string
  open: boolean
  onOpenChange: (next: boolean) => void
  onAcceptAll: (acceptedFields: string[]) => void
}

function SuggestedFixBlock({
  issue,
  onApplyFix,
  open,
  onOpenChange,
  onAcceptAll,
  propertyShowUrl,
}: SuggestedFixBlockProps) {
  const fix: QualitySuggestedFix = issue.suggested_fix ?? {}
  const proposal = fix.proposal ?? null

  switch (issue.issue_type) {
    case "stale_poi":
      return (
        <FixBlockShell label="Suggested fix">
          <p className="text-sm text-muted-foreground">
            Refresh structured fields (hours, website, contact) from OSM Nominatim.
          </p>
          <Button size="sm" onClick={onApplyFix} className="min-h-9">
            <Wand2 className="mr-1 size-3.5" aria-hidden />
            Apply fix (Refresh from OSM)
          </Button>
        </FixBlockShell>
      )

    case "weak_metadata":
      if (proposal) {
        // Field-diff Collapsible.
        return (
          <Collapsible open={open} onOpenChange={onOpenChange}>
            <FixBlockShell label="Suggested fix">
              <CollapsibleTrigger asChild>
                <Button size="sm" variant="outline" className="min-h-9">
                  {open ? (
                    <ChevronDown className="mr-1 size-3.5" aria-hidden />
                  ) : (
                    <ChevronRight className="mr-1 size-3.5" aria-hidden />
                  )}
                  {open ? "Hide proposal" : "Review AI proposal"}
                  {proposal.model && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      Source: {proposal.model.startsWith("claude") ? "Claude" : proposal.model}
                    </span>
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <FieldDiff proposal={proposal} onAcceptAll={onAcceptAll} />
              </CollapsibleContent>
            </FixBlockShell>
          </Collapsible>
        )
      }
      // No proposal yet → CTA to enrich.
      return (
        <FixBlockShell label="Suggested fix">
          <p className="text-sm text-muted-foreground">
            Generate an AI proposal for missing structured fields. You review and accept
            each field individually before anything is written.
          </p>
          <Button size="sm" onClick={onApplyFix} className="min-h-9">
            <Sparkles className="mr-1 size-3.5" aria-hidden />
            Enrich metadata with AI
          </Button>
        </FixBlockShell>
      )

    case "hotel_gap":
      return (
        <FixBlockShell label="Action">
          <p className="text-sm text-muted-foreground">
            Property guests engage with this category, but the regional pool is thin.
            Consider adding public POIs to fill the gap.
          </p>
          {propertyShowUrl && (
            <Button asChild size="sm" variant="outline" className="min-h-9">
              <Link href={propertyShowUrl}>
                <Building2 className="mr-1 size-3.5" aria-hidden />
                View hotel
              </Link>
            </Button>
          )}
        </FixBlockShell>
      )

    default:
      return null
  }
}

function FixBlockShell({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}
